"use client";

import { Button } from "@/components/ui/button";
import { DIM_LABELS as DEFAULT_LABELS } from "@/lib/utils";

const DEFAULT_DIM_ORDER = [
  "slot_completion", "compliance", "naturalness",
  "objection_handling", "goal_achievement", "topic_convergence",
];

interface EvaluationData {
  overall: number;
  dimensions: Record<string, { score: number; detail: string }>;
  suggestions: string[];
}

function scoreColor(score: number): string {
  if (score >= 0.8) return "bg-green-500";
  if (score >= 0.6) return "bg-yellow-500";
  return "bg-red-500";
}

function overallColor(score: number): string {
  if (score >= 0.8) return "text-green-600 dark:text-green-400";
  if (score >= 0.6) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
}

interface Props {
  evaluation: EvaluationData | null | undefined;
  onReEvaluate: () => void;
  loading: boolean;
  dimOrder?: string[];
  dimLabels?: Record<string, string>;
}

export function EvaluationPanel({ evaluation, onReEvaluate, loading, dimOrder, dimLabels }: Props) {
  const order = dimOrder ?? DEFAULT_DIM_ORDER;
  const labels = dimLabels ?? DEFAULT_LABELS;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium">质量评估</h3>
        <Button variant="outline" size="sm" onClick={onReEvaluate} disabled={loading}>
          {loading ? "评估中..." : evaluation ? "重新评估" : "评估"}
        </Button>
      </div>

      {!evaluation ? (
        <p className="text-sm text-muted-foreground">未执行</p>
      ) : (
        <>
          <div className="text-center mb-4">
            <span className={`text-3xl font-bold ${overallColor(evaluation.overall)}`}>
              {(evaluation.overall * 100).toFixed(0)}
            </span>
            <span className="text-sm text-muted-foreground ml-1">/ 100</span>
          </div>

          <div className="space-y-3">
            {order.map((key) => {
              const dim = evaluation.dimensions[key];
              if (!dim) return null;
              return (
                <div key={key}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs w-16 shrink-0">{labels[key] ?? key}</span>
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${scoreColor(dim.score)}`}
                        style={{ width: `${dim.score * 100}%` }}
                      />
                    </div>
                    <span className="text-xs w-8 text-right">{(dim.score * 100).toFixed(0)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 pl-1 border-l-2">
                    {dim.detail}
                  </p>
                </div>
              );
            })}
          </div>

          {evaluation.suggestions.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-medium mb-1">建议</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                {evaluation.suggestions.map((s, i) => (
                  <li key={i}>- {s}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
